import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { suppliersApi, type Supplier, type SupplierReview } from "@/lib/api";
import ComplianceCard from "./components/ComplianceCard";

type Recommendation = "Auto-Renew" | "Conditional Renewal" | "Re-Advertise";

const recColors: Record<Recommendation, { bg: string; text: string; dot: string }> = {
  "Auto-Renew": { bg: "bg-secondary-50", text: "text-secondary-700", dot: "bg-secondary-500" },
  "Conditional Renewal": { bg: "bg-accent-50", text: "text-accent-700", dot: "bg-accent-500" },
  "Re-Advertise": { bg: "bg-foreground-100", text: "text-foreground-600", dot: "bg-foreground-400" },
};

const SCORE_KEYS = [
  "quality_of_service",
  "timeliness_delivery",
  "communication",
  "value_for_money",
  "compliance",
];

const SCORE_LABELS: Record<string, string> = {
  quality_of_service: "Quality of Service",
  timeliness_delivery: "Timeliness & Delivery",
  communication: "Communication",
  value_for_money: "Value for Money",
  compliance: "Compliance",
};

function getScoreColor(score: number) {
  if (score >= 4) return "bg-secondary-500";
  if (score >= 2.5) return "bg-accent-500";
  return "bg-foreground-400";
}

function getScoreText(score: number) {
  if (score >= 4) return "text-secondary-600";
  if (score >= 2.5) return "text-accent-600";
  return "text-foreground-500";
}

function renderStars(score: number, size: "sm" | "md" = "sm") {
  const sz = size === "md" ? "text-base" : "text-xs";
  const full = Math.floor(score);
  const half = score - full >= 0.5;
  return Array.from({ length: 5 }, (_, i) => (
    <i key={i} className={`${sz} ${i < full ? "ri-star-fill text-accent-500" : i === full && half ? "ri-star-half-line text-accent-500" : "ri-star-line text-foreground-300"}`}></i>
  ));
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

export default function SupplierPerformance() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [reviews, setReviews] = useState<SupplierReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const supplierId = Number(id);
    if (isNaN(supplierId)) { setError("Invalid supplier"); setLoading(false); return; }

    Promise.all([
      suppliersApi.get(supplierId),
      suppliersApi.reviews(supplierId),
    ]).then(([supplierRes, reviewsRes]) => {
      setSupplier(supplierRes.data);
      setReviews(reviewsRes.data.data);
    }).catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-foreground-400 text-sm">
        <i className="ri-loader-4-line animate-spin mr-2"></i>Loading...
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <i className="ri-emotion-sad-line text-foreground-300 text-5xl mb-4"></i>
        <p className="text-foreground-500 text-sm">{error ?? "Supplier not found."}</p>
        <button onClick={() => navigate("/supplier-reviews")}
          className="mt-4 h-9 px-4 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors cursor-pointer">
          Back to Supplier Reviews
        </button>
      </div>
    );
  }

  const avgRating = parseFloat(supplier.average_rating || "0");
  const latestReview = reviews[0];
  const latestRec = latestReview?.recommendation as Recommendation | undefined;
  const rc = latestRec ? recColors[latestRec] : null;

  // Trend data from reviews
  const trend = reviews.slice(0, 6).reverse().map((r) => ({
    label: r.quarter && r.year ? `Q${r.quarter}'${String(r.year).slice(2)}` : formatDate(r.review_date),
    score: parseFloat(r.rating),
  }));
  const maxScore = Math.max(...trend.map((t) => t.score), 5);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/supplier-reviews")}
            className="w-8 h-8 rounded-md flex items-center justify-center border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-sm"></i>
          </button>
          <div>
            <h1 className="text-primary-600 text-xl md:text-2xl font-bold">{supplier.name}</h1>
            <p className="text-foreground-500 text-sm">
              {supplier.total_reviews} review{supplier.total_reviews !== 1 ? "s" : ""} · Avg rating:{" "}
              <span className={`font-semibold ${getScoreText(avgRating)}`}>{avgRating.toFixed(1)}</span>
            </p>
          </div>
        </div>
        <button onClick={() => navigate(`/supplier-reviews/new?supplier_id=${supplier.id}`)}
          className="h-9 px-4 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer">
          <i className="ri-add-line text-sm"></i><span>New Review</span>
        </button>
      </div>

      <ComplianceCard supplier={supplier} onUpdated={setSupplier} />

      {/* Summary Panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Average Rating */}
        <div className="bg-white rounded-lg border border-background-200 p-5 flex flex-col items-center justify-center">
          <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-2">Overall Average</p>
          <div className={`w-16 h-16 rounded-full ${getScoreColor(avgRating)} flex items-center justify-center mb-2`}>
            <span className="text-white text-xl font-bold">{avgRating.toFixed(1)}</span>
          </div>
          <div className="flex mb-1">{renderStars(avgRating, "md")}</div>
          <p className="text-foreground-400 text-xs">across {supplier.total_reviews} reviews</p>
        </div>

        {/* Latest Recommendation */}
        <div className="bg-white rounded-lg border border-background-200 p-5 flex flex-col items-center justify-center">
          <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-2">Latest Recommendation</p>
          {rc && latestRec ? (
            <>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold mb-2 ${rc.bg} ${rc.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${rc.dot}`}></span>{latestRec}
              </span>
              {latestReview.quarter && latestReview.year && (
                <p className="text-foreground-500 text-xs">Q{latestReview.quarter} {latestReview.year}</p>
              )}
              {latestReview.reviewed_by && (
                <p className="text-foreground-400 text-xs mt-1">by {latestReview.reviewed_by.name}</p>
              )}
            </>
          ) : (
            <p className="text-foreground-400 text-sm">No reviews yet</p>
          )}
        </div>

        {/* Trend */}
        <div className="bg-white rounded-lg border border-background-200 p-5">
          <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-3">Rating Trend</p>
          {trend.length > 0 ? (
            <div className="flex items-end gap-1.5 h-20">
              {trend.map((t, i) => {
                const h = (t.score / maxScore) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                    <span className="text-foreground-700 text-[10px] font-semibold">{t.score.toFixed(1)}</span>
                    <div className="w-full rounded-t-sm" style={{ height: `${h}%`, backgroundColor: t.score >= 4 ? "oklch(var(--secondary-500))" : t.score >= 2.5 ? "oklch(var(--accent-500))" : "oklch(var(--foreground-400))" }}></div>
                    <span className="text-foreground-400 text-[9px] whitespace-nowrap">{t.label}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-foreground-400 text-sm text-center mt-6">No history yet</p>
          )}
        </div>
      </div>

      {/* Latest Review Detail */}
      {latestReview?.scores && Object.keys(latestReview.scores).length > 0 && (
        <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5 mb-6">
          <h2 className="text-primary-600 text-base font-semibold mb-4">
            Latest Review {latestReview.quarter && latestReview.year ? `— Q${latestReview.quarter} ${latestReview.year}` : `— ${formatDate(latestReview.review_date)}`}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
            {SCORE_KEYS.map((key) => {
              const score = latestReview.scores?.[key] ?? 0;
              return (
                <div key={key} className="bg-background-50 rounded-lg border border-background-200 p-3">
                  <p className="text-foreground-500 text-xs font-medium mb-2">{SCORE_LABELS[key]}</p>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-lg font-bold ${getScoreText(score)}`}>{score}</span>
                    <span className="text-foreground-400 text-xs">/ 5</span>
                  </div>
                  <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${getScoreColor(score)}`} style={{ width: `${(score / 5) * 100}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
          {latestReview.review_text && (
            <div className="bg-background-50 rounded-lg border border-background-200 p-4">
              <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-1.5">Reviewer Comments</p>
              <p className="text-foreground-700 text-sm leading-relaxed">{latestReview.review_text}</p>
            </div>
          )}
        </div>
      )}

      {/* Review History */}
      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
        <h2 className="text-primary-600 text-base font-semibold mb-4">Review History</h2>
        {reviews.length === 0 ? (
          <p className="text-foreground-400 text-sm text-center py-6">No reviews recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200 bg-background-100">
                  <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Period</th>
                  <th className="text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Overall</th>
                  <th className="text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Recommendation</th>
                  <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Reviewer</th>
                  <th className="text-right text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Date</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((r) => {
                  const rec = r.recommendation as Recommendation | null;
                  const rc2 = rec ? recColors[rec] : null;
                  const score = parseFloat(r.rating);
                  return (
                    <tr key={r.id} className="border-b border-background-100 hover:bg-background-100 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-foreground-800 text-sm font-medium">
                          {r.quarter && r.year ? `Q${r.quarter} ${r.year}` : formatDate(r.review_date)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(score)} text-white`}>
                          {score.toFixed(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {rc2 && rec ? (
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${rc2.bg} ${rc2.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${rc2.dot}`}></span>{rec}
                          </span>
                        ) : <span className="text-foreground-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-foreground-600 text-sm hidden lg:table-cell">
                        {r.reviewed_by?.name ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-foreground-400 text-sm hidden sm:table-cell">
                        {formatDate(r.review_date)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
